import { supabase } from '../supabase/config';

export const leaveEventConfirm = (eventId) => {
  window.dispatchEvent(new CustomEvent('openConfirmModal', {
    detail: {
      icon: '🚪',
      iconBg: '#fff3e0',
      title: 'Leave Event',
      message: 'Are you sure you want to leave this event? You can rejoin later if spots are available.',
      confirmText: 'Leave Event',
      cancelText: 'Stay',
      confirmColor: '#ea4335',
      confirmHoverColor: '#d33426',
      onConfirm: async () => {
        if (window.performLeaveEvent) await window.performLeaveEvent(eventId);
      }
    }
  }));
};

// Perform the actual leave operation
export const performLeaveEvent = async (eventId) => {
  const currentUserId = window.currentAppUser?.uid || window.currentUser?.uid;
  
  if (!currentUserId) {
    window.showToast('Please log in to leave events', 'error');
    return;
  }
  
  // Close the confirmation modal
  const modal = document.getElementById('leaveConfirmModal');
  if (modal) modal.remove();
  
  try {
    const supabaseClient = window.supabase || supabase;
    
    // Temporarily disable real-time updates for this event
    window.skipRealtimeUpdate = eventId;
    
    // Call the RPC function to leave the event
    const { data: rpcResult, error: rpcError } = await supabaseClient.rpc('leave_event', {
      event_id_param: eventId,
      user_id_param: currentUserId
    });
    
    if (rpcError) {
      window.skipRealtimeUpdate = null; // Re-enable
      throw rpcError;
    }
    
    if (!rpcResult.success) {
      console.warn('⚠️', rpcResult.message);
      window.showToast(rpcResult.message, 'error');
      window.skipRealtimeUpdate = null; // Re-enable
      return;
    }

    // Get event data to notify organizer
    const { data: eventDataForNotif } = await supabaseClient
      .from('events')
      .select('title, creator_id')
      .eq('event_id', eventId)
      .single();
    
    // Notify organizer that someone left their event
    const leaverName = window.currentAppUser?.profile?.name || 'Someone';
    if (eventDataForNotif && eventDataForNotif.creator_id !== currentUserId) {
      const eventTitle = eventDataForNotif.title || 'your event';
      
      // Create notification for organizer
      await supabaseClient
        .from('notifications')
        .insert({
          user_id: eventDataForNotif.creator_id,
          type: 'user_left',
          title: 'Participant Left',
          message: `${leaverName} left your event "${eventTitle}"`,
          event_id: eventId,
          event_title: eventTitle,
          requester_id: currentUserId,
          requester_name: leaverName,
          requester_avatar: window.currentAppUser?.profile?.profilePicture || null
        });
    }
    
    // Wait for database propagation
    await new Promise(resolve => setTimeout(resolve, 500));
    
    // Trigger React state update to remove from active events
    window.dispatchEvent(new CustomEvent('eventJoined', { 
      detail: { eventId, userId: currentUserId, skipMarkerRefresh: true }
    }));
    
    // Get fresh event data for panel
    const { data: freshEventData, error: refreshError } = await supabaseClient
      .from('events')
      .select('*')
      .eq('event_id', eventId)
      .single();
    
    if (!refreshError && freshEventData) {
      const formattedEvent = {
        eventId: freshEventData.event_id,
        title: freshEventData.title,
        description: freshEventData.description,
        placeId: freshEventData.place_id,
        placeName: freshEventData.place_name,
        placeAddress: freshEventData.place_address,
        placePhotoUrl: freshEventData.place_photo_url,
        latitude: freshEventData.latitude,
        longitude: freshEventData.longitude,
        startDateTime: freshEventData.start_date_time,
        endDateTime: freshEventData.end_date_time,
        peopleLimit: freshEventData.people_limit,
        creatorId: freshEventData.creator_id,
        createdBy: freshEventData.created_by,
        participants: freshEventData.participants,
        customLabel: freshEventData.custom_label,
        requireApprove: freshEventData.require_approve,
        pendingRequests: freshEventData.pending_requests
      };
      
      const eventDetailsPanel = document.getElementById('eventDetailsPanel');
      if (eventDetailsPanel) {
        eventDetailsPanel.style.right = '-400px';
        setTimeout(() => {
          eventDetailsPanel.remove();
          
          setTimeout(() => {
window.showEventDetailsPanel(formattedEvent, freshEventData.place_photo_url || '');
            
            // Refresh marker and re-enable real-time updates
            setTimeout(async () => {
              if (window.refreshSingleEventMarker) {
                await window.refreshSingleEventMarker(eventId);
              }
              window.skipRealtimeUpdate = null;
            }, 500);
          }, 100);
        }, 300);
      } else {
        // Re-enable if panel doesn't exist
        window.skipRealtimeUpdate = null;
      }
    } else {
      // Re-enable on error
      window.skipRealtimeUpdate = null;
    }
  } catch (err) {
    window.showToast('Failed to leave event. Please try again.', 'error');
    window.skipRealtimeUpdate = null; // Re-enable
  }
};

// Remove specific event markers from the map (without full refresh)
export const removeEventMarkers = (eventIds) => {
  if (!Array.isArray(eventIds) || eventIds.length === 0) return;
  
  eventIds.forEach(eventId => {
    // Remove marker from map if it exists
    if (window.eventMarkers && window.eventMarkers[eventId]) {
      window.eventMarkers[eventId].remove();
      delete window.eventMarkers[eventId];
    }
    
    // Close event details panel if this event is currently open
    const eventDetailsPanel = document.getElementById('eventDetailsPanel');
    if (eventDetailsPanel && eventDetailsPanel.dataset.eventId === eventId) {
      eventDetailsPanel.remove();
    }
  });
  
  // Update user's event lists (remove from their active/archived events)
  if (window.loadUserEvents) {
    window.loadUserEvents();
  }
};

export const showUserProfileFromRequest = (userId, eventId) => {
  // Hide the join request popup (dispatch hide event)
  window.dispatchEvent(new CustomEvent('hideJoinRequestPopup'));
  
  // Show user profile panel
  window.showUserProfilePanel(userId, 'request');
  
  // When profile closes, restore the request popup
  const handleProfileClose = () => {
    window.dispatchEvent(new CustomEvent('restoreJoinRequestPopup'));
    window.removeEventListener('closeUserProfilePanel', handleProfileClose);
  };
  window.addEventListener('closeUserProfilePanel', handleProfileClose);
};

export const showJoinRequestPopup = async (eventId, requesterId) => {
  const supabaseClient = window.supabase || supabase;
  
  const { data: requesterData, error } = await supabaseClient
    .from('users')
    .select('id, name, profile_picture, date_of_birth')
    .eq('id', requesterId)
    .single();
  
  if (error || !requesterData) return;
  
  const birthDate = new Date(requesterData.date_of_birth);
  const age = new Date().getFullYear() - birthDate.getFullYear();
  const fallbackAvatar = `https://ui-avatars.com/api/?name=${encodeURIComponent(requesterData.name || 'User')}&background=e8eaed&color=5f6368&size=80`;
  
  window.dispatchEvent(new CustomEvent('openJoinRequestPopup', {
    detail: {
      eventId,
      requesterId,
      requesterName: requesterData.name,
      requesterAvatar: requesterData.profile_picture || fallbackAvatar,
      requesterAge: age
    }
  }));
};

export const notifyOrganizerOfRequest = async (organizerId, eventId, requesterId) => {

  try {
    const supabaseClient = window.supabase || supabase;
    
    // Get event and requester info
    const { data: eventData } = await supabaseClient
      .from('events')
      .select('title')
      .eq('event_id', eventId)
      .single();
    
    const { data: requesterData } = await supabaseClient
      .from('users')
      .select('name, profile_picture')
      .eq('id', requesterId)
      .single();
    
    const eventTitle = eventData?.title || 'your event';
    const requesterName = requesterData?.name || 'Someone';
    
    // Create notification in database
    await supabaseClient
      .from('notifications')
      .insert({
        user_id: organizerId,
        type: 'join_request',
        title: 'New Join Request',
        message: `${requesterName} wants to join "${eventTitle}"`,
        event_id: eventId,
        event_title: eventTitle,
        requester_id: requesterId,
        requester_name: requesterName,
        requester_avatar: requesterData?.profile_picture
      });
    
    // Update requests count
    if (window.updateRequestsCount) {
      window.updateRequestsCount();
    }
    
    // Update notification count
    if (window.updateNotificationCount) {
      window.updateNotificationCount();
    }
    
  } catch (error) {
    console.error('Error notifying organizer:', error);
  }
};

// Function to join an event
export const joinEvent = async (eventId) => {
  const currentUserId = window.currentAppUser?.uid || window.currentUser?.uid;
  
  if (!currentUserId) {
    window.showToast('Please log in to join events', 'error');
    return;
  }
  
  const button = document.getElementById(`joinButton_${eventId}`);
  
  // Disable button during processing
  if (button) {
    button.disabled = true;
    button.style.opacity = '0.6';
    button.style.cursor = 'wait';
  }
  
  try {
    const supabaseClient = window.supabase || supabase;
// First, check if event requires approval
    const { data: eventData, error: eventError } = await supabaseClient
      .from('events')
      .select('require_approve, pending_requests, creator_id, participants, people_limit')
      .eq('event_id', eventId)
      .single();
    
    if (eventError) {
      throw eventError;
    }
    
    const requireApprove = eventData.require_approve || false;
    
    if (requireApprove) {
      const pendingRequests = eventData.pending_requests || [];
      
      // Check if already requested
      if (pendingRequests.includes(currentUserId)) {
        window.showToast('You have already requested to join this event. Please wait for approval.', 'warning');
        if (button) {
          button.disabled = false;
          button.style.opacity = '1';
          button.style.cursor = 'pointer';
        }
        return;
      }
      
      // Check if already a participant
      if (eventData.participants?.includes(currentUserId)) {
        window.showToast('You are already a participant of this event.', 'warning');
        if (button) {
          button.disabled = false;
          button.style.opacity = '1';
          button.style.cursor = 'pointer';
        }
        return;
      }
      
      // Add to pending requests using RPC function
      const { data: rpcResult, error: updateError } = await supabaseClient
        .rpc('add_pending_request', {
          event_id_param: eventId,
          user_id_param: currentUserId
        });
      
      if (updateError) {
        console.error('❌ Error sending join request:', updateError);
        window.showToast('Failed to send join request. Please try again.', 'error');
        if (button) {
          button.disabled = false;
          button.style.opacity = '1';
          button.style.cursor = 'pointer';
        }
        return;
      }
      
      // Update button to show "Pending"
      let buttonElement = button || 
                         document.getElementById(`joinButton_${eventId}`) ||
                         document.querySelector(`[onclick*="joinEvent('${eventId}')"]`);
      
      if (buttonElement) {
        buttonElement.textContent = 'Pending';
        buttonElement.disabled = true;
        buttonElement.style.opacity = '0.5';
        buttonElement.style.cursor = 'not-allowed';
      } else {
        console.error('❌ Could not find button to update!');
      }
      
      window.showToast('Join request sent! Waiting for organizer approval.', 'info');
      
      // Notify organizer if they're online
      window.notifyOrganizerOfRequest(eventData.creator_id, eventId, currentUserId);
      
      // Update the "Requires Approval" indicator to show request is pending
      const requiresApprovalDiv = document.querySelector('[data-requires-approval]');
      if (requiresApprovalDiv) {
        requiresApprovalDiv.innerHTML = `
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#856404" stroke-width="2">
            <rect x="3" y="3" width="18" height="18" rx="2" ry="2"></rect>
            <path d="M9 11l3 3 5-5"></path>
          </svg>
          <span style="
            font-size: 13px;
            font-weight: 600;
            color: #856404;
          ">Requires Approval</span>
        `;
      }
      
      return; // Exit early - don't continue with normal join flow
    }
    
    // Call the RPC function to join the event
    const { data: rpcResult, error: rpcError } = await supabaseClient.rpc('join_event', {
      event_id_param: eventId,
      user_id_param: currentUserId
    });
    
    if (rpcError) {
      throw rpcError;
    }
    
    if (!rpcResult.success) {
      window.showToast(rpcResult.message, 'error');
      if (button) {
        button.disabled = false;
        button.style.opacity = '1';
        button.style.cursor = 'pointer';
      }
      return;
    }
    
        // Notify organizer that someone joined their event
    const joinerName = window.currentAppUser?.profile?.name || 'Someone';
    if (eventData.creator_id !== currentUserId) {
      // Fetch event title for notification
      const { data: eventInfo } = await supabaseClient
        .from('events')
        .select('title')
        .eq('event_id', eventId)
        .single();
      
      const eventTitle = eventInfo?.title || 'your event';
      
// Create notification for organizer
      await supabaseClient
        .from('notifications')
        .insert({
          user_id: eventData.creator_id,
          type: 'user_joined',
          title: 'New Participant',
          message: `${joinerName} joined your event "${eventTitle}"`,
          event_id: eventId,
          event_title: eventTitle,
          requester_id: currentUserId,
          requester_name: joinerName,
          requester_avatar: window.currentAppUser?.profile?.profilePicture || null
        });
      
      // Trigger notification count update for organizer (if they're the current user viewing)
      if (window.updateNotificationCount) {
        window.updateNotificationCount();
      }
    }
    
    // Get fresh event data
    const { data: verifyData } = await supabaseClient
      .from('events')
      .select('participants, people_limit, place_id')
      .eq('event_id', eventId)
      .single();
    
    // Animate button transformation to Chat
    if (button) {
      button.style.transform = 'scale(0.8)';
      button.style.opacity = '0';
      
      setTimeout(() => {
        button.id = `chatButton_${eventId}`;
        button.innerHTML = `
          Chat
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
            <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"></path>
          </svg>
        `;
        button.style.background = 'white';
        button.style.border = '2px solid rgba(97, 97, 97, 1)';
        button.onclick = async () => {
          const { data: eventData } = await supabaseClient.from('events').select('title').eq('event_id', eventId).single();
          window.openEventChat(eventId, eventData?.title || 'Event');
        };
        button.disabled = false;
        button.style.cursor = 'pointer';
        
        button.onmouseover = function() {
          this.style.transform = 'scale(1.02)';
          this.style.boxShadow = '3px 3px 8px rgba(0, 0, 0, 0.5)';
        };
        button.onmouseout = function() {
          this.style.transform = 'scale(1)';
          this.style.boxShadow = '2px 2px 6px rgba(0, 0, 0, 0.4)';
        };
        
        button.style.transform = 'scale(1)';
        button.style.opacity = '1';
      }, 300);
    }

    // Immediately update participant count to show the join happened
    const participantsCountSpan = document.querySelector('[data-participants-count]');
    if (participantsCountSpan && verifyData) {
      const newCount = verifyData.participants.length;
      const limit = verifyData.people_limit;
      if (limit === null || limit === undefined) {
        participantsCountSpan.textContent = `${newCount} / ∞`;
      } else {
        participantsCountSpan.textContent = `${newCount} / ${limit}`;
      }
    }
    
    // Immediately hide the last participant element (dots or +N button) to prevent flicker
    const participantsRow = document.querySelector('[data-participants-row]');
    if (participantsRow) {
      const lastChild = participantsRow.lastElementChild;
      if (lastChild) {
        lastChild.style.display = 'none'; // Hide it completely instead of fading
      }
    }
    
    // Update participants count
    setTimeout(() => {
      if (verifyData) {
        const participantsCountSpan = document.querySelector('[data-participants-count]');
        if (participantsCountSpan) {
          const newCount = verifyData.participants.length;
          const limit = verifyData.people_limit;
          if (limit === null || limit === undefined) {
            participantsCountSpan.textContent = `${newCount} / ∞`;
          } else {
            participantsCountSpan.textContent = `${newCount} / ${limit}`;
          }
        }
      }
    }, 100);

// 3. Refresh event details panel WITHOUT closing it
setTimeout(async () => {
  const { data: freshEventData, error: refreshError } = await supabaseClient
    .from('events')
    .select('*')
    .eq('event_id', eventId)
    .single();
  
  if (!refreshError && freshEventData) {
    const formattedEvent = {
      eventId: freshEventData.event_id,
      title: freshEventData.title,
      description: freshEventData.description,
      placeId: freshEventData.place_id,
      placeName: freshEventData.place_name,
      placeAddress: freshEventData.place_address,
      placePhotoUrl: freshEventData.place_photo_url,
      latitude: freshEventData.latitude,
      longitude: freshEventData.longitude,
      startDateTime: freshEventData.start_date_time,
      endDateTime: freshEventData.end_date_time,
      peopleLimit: freshEventData.people_limit,
      creatorId: freshEventData.creator_id,
      createdBy: freshEventData.created_by,
      participants: freshEventData.participants,
      customLabel: freshEventData.custom_label,
      requireApprove: freshEventData.require_approve,
      pendingRequests: freshEventData.pending_requests
    };
    
    // Get the current panel
    const eventDetailsPanel = document.getElementById('eventDetailsPanel');
    if (eventDetailsPanel) {
      // Generate new panel HTML (you'll need to call showEventDetailsPanel's HTML generation)
      // For now, let's just update the specific parts that changed
      
      // Update participants count
      const participantsCountSpan = eventDetailsPanel.querySelector('[data-participants-count]');
      if (participantsCountSpan) {
        const newCount = freshEventData.participants.length;
        const limit = freshEventData.people_limit;
        if (limit === null || limit === undefined) {
          participantsCountSpan.textContent = `${newCount} / ∞`;
        } else {
          participantsCountSpan.textContent = `${newCount} / ${limit}`;
        }
      }

// Update title with checkmark
const titleElement = eventDetailsPanel.querySelector('h2');
if (titleElement) {
  const isParticipant = freshEventData.participants.includes(currentUserId);
  const isOrganizer = freshEventData.creator_id === currentUserId;
  
  if (isParticipant || isOrganizer) {
    if (!titleElement.innerHTML.includes('✓')) {
      titleElement.style.display = 'flex';
      titleElement.style.alignItems = 'center';
      titleElement.style.gap = '8px';
      
      titleElement.innerHTML = `
        Event Details
        <div style="
          width: 25px;
          height: 25px;
          background: white;
          border: 2px solid rgba(52, 168, 83, 1);
          border-radius: 30%;
          display: flex;
          align-items: center;
          justify-content: center;
          box-shadow: 2px 2px 6px rgba(0, 0, 0, 0.4);
          margin-bottom: -3px;
          flex-shrink: 0;
        ">
          <span style="font-size: 16px; line-height: 1; color: green;">✓</span>
        </div>
      `;
    }
  }
}
      
      // Update participants row by regenerating it
      const participantsRow = eventDetailsPanel.querySelector('[data-participants-row]');
      if (participantsRow && freshEventData.participants) {
        // DON'T clear yet - build new content first
        const newAvatars = [];
        
        // Fetch participant data
        const participantIds = freshEventData.participants.filter(id => id !== freshEventData.creator_id);
        const maxVisible = 5;
        const visibleIds = participantIds.slice(0, maxVisible);
        
        for (const participantId of visibleIds) {
          const { data: userData } = await supabaseClient
            .from('users')
            .select('id, name, profile_picture, online_status, last_seen')
            .eq('id', participantId)
            .single();
          
          if (userData) {
            const isOnline = userData.online_status === 'online' && 
              userData.last_seen && 
              (new Date() - new Date(userData.last_seen)) < 120000;
            const statusColor = isOnline ? '#34a853' : '#9e9e9e';
            const borderColor = isOnline ? '#34a853' : '#9e9e9e';
            const fallbackAvatar = `https://ui-avatars.com/api/?name=${encodeURIComponent(userData.name || 'User')}&background=e8eaed&color=5f6368&size=40`;
            
            const avatar = document.createElement('div');
            avatar.className = 'participant-avatar';
            avatar.style.cssText = 'flex-shrink: 0; position: relative;';
            avatar.onclick = () => window.showUserProfilePanel(userData.id, 'eventDetails');
            avatar.innerHTML = `
              <img 
                src="${userData.profile_picture || fallbackAvatar}"
                alt="${userData.name}"
                style="
                  width: 40px;
                  height: 40px;
                  border-radius: 50%;
                  object-fit: cover;
                  border: 2px solid ${borderColor};
                "
                onerror="this.src='${fallbackAvatar}'"
              />
              <div style="
                position: absolute;
                bottom: 0;
                right: 0;
                width: 12px;
                height: 12px;
                background: ${statusColor};
                border: 2px solid white;
                border-radius: 50%;
                box-shadow: 0 1px 2px rgba(0,0,0,0.2);
              "></div>
            `;
            newAvatars.push(avatar);
          }
        }

        // Now clear and add all at once
        participantsRow.innerHTML = '';
        newAvatars.forEach(avatar => participantsRow.appendChild(avatar));
        
        // Add +N button or waiting dots
        const remainingCount = participantIds.length - maxVisible;
const limitReached = freshEventData.people_limit && freshEventData.participants.length >= freshEventData.people_limit;

// Check if event has ended
const now = new Date();
const endDate = freshEventData.end_date_time ? new Date(freshEventData.end_date_time) : null;
const hasEnded = endDate && now >= endDate;
        
        if (remainingCount > 0) {
          const moreButton = document.createElement('div');
          moreButton.style.cssText = `
            width: 40px;
            height: 40px;
            border-radius: 50%;
            background: #dddddd;
            border: 2px solid #e8eaed;
            display: flex;
            align-items: center;
            justify-content: center;
            cursor: pointer;
            flex-shrink: 0;
            font-size: 12px;
            font-weight: 600;
            color: #202124;
          `;
          moreButton.textContent = `+${remainingCount}`;
          moreButton.onclick = () => window.showAllParticipantsList(formattedEvent);
          participantsRow.appendChild(moreButton);
        } else if (!limitReached) {
          const waitingDots = document.createElement('div');
          waitingDots.style.cssText = `
            width: 40px;
            height: 40px;
            border-radius: 50%;
            background: #dddddd;
            border: 2px solid #e8eaed;
            display: flex;
            align-items: center;
            justify-content: center;
            gap: 3px;
            flex-shrink: 0;
          `;

            const dotClass = hasEnded ? '' : 'class="wave-dot"';  // ← Conditionally add animation
            const title = hasEnded ? 'Event finished' : 'Waiting for joiners...';
            waitingDots.title = title;

          waitingDots.innerHTML = `
            <div ${dotClass} style="width: 4px; height: 4px; border-radius: 50%; background: #5f6368;"></div>
            <div ${dotClass} style="width: 4px; height: 4px; border-radius: 50%; background: #5f6368;"></div>
            <div ${dotClass} style="width: 4px; height: 4px; border-radius: 50%; background: #5f6368;"></div>
          `;
          participantsRow.appendChild(waitingDots);
        }
      }
      
                  // Update action buttons to show Leave button
      let actionButtons = document.getElementById('actionButtons');
      
      // If actionButtons doesn't exist (user wasn't participant before), create it
      if (!actionButtons) {
        actionButtons = document.createElement('div');
        actionButtons.id = 'actionButtons';
        actionButtons.style.cssText = `
          position: absolute;
          bottom: 0;
          left: 0;
          right: 0;
          padding: 16px 24px;
          background: white;
          border-top: 1px solid #e5e7eb;
          box-shadow: 0 -2px 8px rgba(0, 0, 0, 0.1);
        `;
        eventDetailsPanel.appendChild(actionButtons);
      }
      if (actionButtons) {
        const isOrganizer = freshEventData.creator_id === currentUserId;
        const isParticipant = freshEventData.participants.includes(currentUserId) && !isOrganizer;
        
        if (isParticipant) {
          // Show Leave button for new participant
          actionButtons.innerHTML = `
            <div style="
              display: flex;
              justify-content: center;
            ">
              <button
                onclick="window.leaveEventConfirm('${freshEventData.event_id}')"
                style="
                  width: 280px;
                  padding: 12px 20px;
                  background: #fef7f7;
                  color: rgba(255, 23, 0, 1);
                  border: 2px solid rgba(255, 23, 0, 1);
                  border-radius: 30px;
                  font-size: 15px;
                  font-weight: 600;
                  cursor: pointer;
                  transition: all 0.2s;
                  display: flex;
                  align-items: center;
                  justify-content: center;
                  gap: 8px;
                  box-shadow: rgba(0, 0, 0, 0.4) 2px 2px 6px;
                "
                onmouseover="this.style.background='#fa050533';"
                onmouseout="this.style.background='#fef7f7';"
              >
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                  <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4"></path>
                  <polyline points="16 17 21 12 16 7"></polyline>
                  <line x1="21" y1="12" x2="9" y2="12"></line>
                </svg>
                Leave Event
              </button>
            </div>
          `;
        }
      }
    }
  }
}, 500);

  } catch (err) {
    window.showToast('Failed to join event. Please try again.', 'error');
    
    if (button) {
      button.disabled = false;
      button.style.opacity = '1';
      button.style.cursor = 'pointer';
      button.style.transform = 'scale(1)';
    }

        
    // Re-enable real-time on error
    window.skipRealtimeUpdate = null;
    return; // Exit early on error
  }

  // Temporarily disable real-time updates
  window.skipRealtimeUpdate = eventId;
  
  // Dispatch event for profile panel refresh ONLY (skip marker refresh)
  window.dispatchEvent(new CustomEvent('eventJoined', { 
    detail: { 
      eventId, 
      userId: currentUserId,
      skipMarkerRefresh: true  // Flag to skip marker recreation
    }
  }));

// Refresh the marker so clicking it shows fresh data
  setTimeout(async () => {
    if (window.refreshSingleEventMarker) {
      await window.refreshSingleEventMarker(eventId);
    }
    window.skipRealtimeUpdate = null;
  }, 1500);
};

export const handleJoinRequest = async (eventId, requesterId, action) => {
  const supabaseClient = window.supabase || supabase;
  
  // Get event data
  const { data: eventData, error: fetchError } = await supabaseClient
    .from('events')
    .select('*')
    .eq('event_id', eventId)
    .single();
  
  if (fetchError || !eventData) {
    window.showToast('Failed to process request. Please try again.', 'error');
    return;
  }
  
  const pendingRequests = eventData.pending_requests || [];
  const participants = eventData.participants || [];
  
  // Remove from pending requests
  const updatedRequests = pendingRequests.filter(id => id !== requesterId);
  
  let updateData = {
    pending_requests: updatedRequests
  };
  
if (action === 'accept') {
    // Add to participants
    updateData.participants = [...participants, requesterId];
    
    // Delay notification by 1 minute to allow map to refresh
    setTimeout(async () => {
      const { data: eventData } = await supabaseClient
        .from('events')
        .select('title, participants')
        .eq('event_id', eventId)
        .single();
      
      // Verify user is still a participant before sending notification
      if (eventData?.participants?.includes(requesterId)) {
        await supabaseClient
          .from('notifications')
          .insert({
            user_id: requesterId,
            type: 'request_accepted',
            title: 'Request Accepted',
            message: `Your request to join "${eventData?.title || 'an event'}" was accepted!`,
            event_id: eventId,
            event_title: eventData?.title
          });
        
        // Update notification count for the user
        if (window.updateNotificationCount) {
          window.updateNotificationCount();
        }
      }
    }, 60000); // 1m delay
  }

  const { error: updateError } = await supabaseClient
    .from('events')
    .update(updateData)
    .eq('event_id', eventId);
  
  if (updateError) {
    window.showToast('Failed to process request. Please try again.', 'error');
    return;
  }
  
  // JoinRequestPopup (React) closes itself when Accept/Decline is clicked
  
// Update marker inline instead of refreshing (prevents duplicates)
  const placeId = eventData.place_id;
  if (placeId && window.eventMarkersRef.current && action === 'accept') {
    const marker = window.eventMarkersRef.current.find(m => {
      const content = m.content || m.getElement?.() || m._element;
      if (!content) return false;
      const directPlaceId = content.dataset?.placeId;
      const childPlaceId = content.querySelector?.('[data-place-id]')?.dataset?.placeId;
      const markerPlaceId = directPlaceId || childPlaceId;
      return markerPlaceId === placeId;
    });
    
    if (marker) {
      const markerElement = marker.content || marker.getElement?.() || marker._element;
      if (markerElement) {
        // Update participant count in the label
        const participantCountSpans = markerElement.querySelectorAll('span[style*="font-weight: 600"]');
        participantCountSpans.forEach(countSpan => {
          if (countSpan.textContent.includes('/')) {
            const newCount = updateData.participants.length;
            const limit = eventData.people_limit;
            countSpan.textContent = limit ? `${newCount} / ${limit}` : `${newCount} / ∞`;
          }
        });
      }
    }
  }
  
// Disable real-time updates temporarily and refresh marker
  window.skipRealtimeUpdate = eventId;
  setTimeout(async () => {
    if (action === 'accept' && window.refreshSingleEventMarker) {
      await window.refreshSingleEventMarker(eventId);
    }
    window.skipRealtimeUpdate = null;
  }, 1500);
  
  // Refresh requests count
  if (window.updateRequestsCount) {
    window.updateRequestsCount();
  }

  // Trigger requests panel refresh to remove the handled request
  window.dispatchEvent(new CustomEvent('requestHandled', {
    detail: { eventId, userId: requesterId, action }
  }));
};

export const showRequestsPanel = async () => {
  const currentUserId = window.currentAppUser?.uid || window.currentUser?.uid;
  if (!currentUserId) {
    window.showToast('Please log in to view requests', 'info');
    return;
  }
  
  const supabaseClient = window.supabase || supabase;
  
  try {
    // Get all events where current user is organizer and has pending requests
    const { data: events, error } = await supabaseClient
      .from('events')
      .select('*')
      .eq('creator_id', currentUserId);
    
    if (error) throw error;
    
    const requests = [];
    
    // Filter events that have pending requests
    const eventsWithRequests = (events || []).filter(event => 
      event.pending_requests && event.pending_requests.length > 0
    );
    
    for (const event of eventsWithRequests) {
      const pendingUsers = event.pending_requests || [];
      
      for (const userId of pendingUsers) {
        // Fetch user data
        const { data: userData } = await supabaseClient
          .from('users')
          .select('id, name, profile_picture, date_of_birth')
          .eq('id', userId)
          .single();
        
        if (userData) {
          const birthDate = new Date(userData.date_of_birth);
          const age = new Date().getFullYear() - birthDate.getFullYear();
          
          const fallbackAvatar = `https://ui-avatars.com/api/?name=${encodeURIComponent(userData.name || 'User')}&background=e8eaed&color=5f6368&size=48`;
          
          requests.push({
            userId: userData.id,
            userName: userData.name,
            userAvatar: userData.profile_picture || fallbackAvatar,
            userAge: age,
            eventId: event.event_id,
            eventTitle: event.title,
            requestTime: 'Pending' // You can add timestamps if needed
          });
        }
      }
    }
    
    // Dispatch event to React component to update the requests view
    window.dispatchEvent(new CustomEvent('requestsLoaded', { 
      detail: { requests }
    }));
    
    // Update badge count
    window.updateRequestsCount(requests.length);
    
  } catch (error) {
    window.showToast('Failed to load requests. Please try again.', 'error');
  }
};

export const updateRequestsCount = async (count) => {
  // If count not provided, fetch it
  if (count === undefined) {
    const currentUserId = window.currentAppUser?.uid || window.currentUser?.uid;
    if (!currentUserId) return;
    
    const supabaseClient = window.supabase || supabase;
    
    try {
      const { data: events, error } = await supabaseClient
        .from('events')
        .select('pending_requests')
        .eq('creator_id', currentUserId);
      
      if (error) throw error;
      
      // Count total pending requests across all events
      count = 0;
      for (const event of events || []) {
        if (event.pending_requests && event.pending_requests.length > 0) {
          count += event.pending_requests.length;
        }
      }
    } catch (error) {
      return;
    }
  }

  // Update badge
  const badge = document.getElementById('requestsCount');
  if (badge) {
    if (count > 0) {
      badge.textContent = count > 99 ? '99+' : count;
      badge.style.display = 'block';
    } else {
      badge.style.display = 'none';
    }
  }
  
  // Dispatch event for React component
  window.dispatchEvent(new CustomEvent('requestsCountUpdated', { 
    detail: { count }
  }));
};

// Delete all notifications for current user
export const deleteAllNotifications = async () => {
  const currentUserId = window.currentAppUser?.uid || window.currentUser?.uid;
  if (!currentUserId) {
    return;
  }
  
  try {
    const supabaseClient = window.supabase || supabase;

    // First, check how many notifications exist
    const { data: checkData, error: checkError } = await supabaseClient
      .from('notifications')
      .select('id')
      .eq('user_id', currentUserId);
    
    if (checkError) {
      throw checkError;
    }
    
    if (!checkData || checkData.length === 0) {
      window.showToast('No notifications to delete', 'info');
      return;
    }
    
    // Delete all notifications for this user
    const { data: deleteData, error: deleteError } = await supabaseClient
      .from('notifications')
      .delete()
      .eq('user_id', currentUserId)
      .select(); // Return deleted rows to verify
    
    if (deleteError) {
      throw deleteError;
    }
    
// Immediately update all notification badges to 0
    const badgeIds = ['notificationBadge', 'notificationBadgeDesktop', 'notificationBadgeMobile'];
    badgeIds.forEach(id => {
      const badge = document.getElementById(id);
      if (badge) {
        badge.style.display = 'none';
      }
    });
    
    // Dispatch event to update notifications list to empty array
    window.dispatchEvent(new CustomEvent('notificationsLoaded', {
      detail: { notifications: [] }
    }));
    
    window.showToast(`Deleted ${deleteData?.length || 0} notification${deleteData?.length !== 1 ? 's' : ''}`, 'success');
  } catch (error) {
    window.showToast('Failed to delete notifications', 'error');
  }
};

window.leaveEventConfirm = leaveEventConfirm;
window.performLeaveEvent = performLeaveEvent;
window.removeEventMarkers = removeEventMarkers;
window.showUserProfileFromRequest = showUserProfileFromRequest;
window.showJoinRequestPopup = showJoinRequestPopup;
window.notifyOrganizerOfRequest = notifyOrganizerOfRequest;
window.joinEvent = joinEvent;
window.handleJoinRequest = handleJoinRequest;
window.showRequestsPanel = showRequestsPanel;
window.updateRequestsCount = updateRequestsCount;
window.deleteAllNotifications = deleteAllNotifications;